import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';

const MediaPage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [limit, setLimit] = useState('100');
  const [loadingUrl, setLoadingUrl] = useState(null);
  const [previewItem, setPreviewItem] = useState(null); // { s3Key, url, contentType }

  useEffect(() => {
    fetchMedia();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (phoneFilter) queryParams.append('phone_number', phoneFilter);
      if (limit) queryParams.append('limit', limit);

      const url = `${CONFIG.apiBaseUrl}/admin/media${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const resp = await fetch(url, {
        credentials: 'include',
      });

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.detail || `Failed to fetch: ${resp.statusText}`);
      }

      const data = await resp.json();
      setMedia(data.media || []);
    } catch (err) {
      console.error('Error fetching media:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isPreviewable = (contentType) => {
    if (!contentType) return false;
    return contentType.startsWith('image/') || contentType.startsWith('video/');
  };

  const handleViewMedia = async (s3Key, contentType) => {
    // If already previewing this item, close it
    if (previewItem?.s3Key === s3Key) {
      setPreviewItem(null);
      return;
    }

    try {
      setLoadingUrl(s3Key);
      const url = `${CONFIG.apiBaseUrl}/admin/media/url?s3_key=${encodeURIComponent(s3Key)}`;

      const resp = await fetch(url, {
        credentials: 'include',
      });

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.detail || 'Failed to get download URL');
      }

      const data = await resp.json();

      if (isPreviewable(contentType)) {
        setPreviewItem({ s3Key, url: data.url, contentType });
      } else {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Error getting media URL:', err);
      setError('Failed to open media: ' + err.message);
    } finally {
      setLoadingUrl(null);
    }
  };

  const getFilename = (s3Key) => {
    if (!s3Key) return 'Unknown';
    const parts = s3Key.split('/');
    return parts[parts.length - 1];
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTimestamp = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMedia();
  };

  return (
    <div className="whatsapp-responses-page">
      <h2 className="page-title">Media</h2>

      {loading && <div className="loading-message">Loading media...</div>}
      {error && <div className="error-message">Error: {error}</div>}

      {!loading && (
        <>
          <div className="messages-container">
            <div className="page-controls">
              <form onSubmit={handleSearch} className="filter-form">
                <div className="filter-group">
                  <label htmlFor="phoneFilter">Phone Number:</label>
                  <input
                    type="text"
                    id="phoneFilter"
                    value={phoneFilter}
                    onChange={(e) => setPhoneFilter(e.target.value)}
                    placeholder="e.g., +15551234567"
                    className="form-input"
                    style={{ width: '200px' }}
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="limit">Limit:</label>
                  <select
                    id="limit"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    className="status-filter"
                  >
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="500">500</option>
                  </select>
                </div>
                <button type="submit" className="btn-refresh">Search</button>
              </form>
              <button onClick={fetchMedia} className="btn-refresh">Refresh</button>
            </div>

            {media.length === 0 && (
              <div className="no-messages">
                No media files found.{phoneFilter && ' Try a different phone number or clear the filter.'}
              </div>
            )}

            {media.length > 0 && (
              <>
                <div className="messages-count">
                  Showing {media.length} file{media.length !== 1 ? 's' : ''}
                </div>
                <div className="messages-table-wrapper">
                  <table className="messages-table">
                    <thead>
                      <tr>
                        <th>Filename</th>
                        <th>Phone</th>
                        <th>Channel</th>
                        <th>Type</th>
                        <th>Size</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {media.map((item) => {
                        const key = `${item.event_id}_${item.timestamp_id}`;
                        const isPreviewing = previewItem?.s3Key === item.s3_key;
                        return (
                          <React.Fragment key={key}>
                            <tr>
                              <td>
                                <button
                                  onClick={() => handleViewMedia(item.s3_key, item.content_type)}
                                  disabled={loadingUrl === item.s3_key}
                                  style={{
                                    textDecoration: 'underline',
                                    background: 'none',
                                    border: 'none',
                                    color: '#007acc',
                                    cursor: loadingUrl === item.s3_key ? 'wait' : 'pointer',
                                    padding: 0,
                                    font: 'inherit',
                                  }}
                                >
                                  {loadingUrl === item.s3_key ? 'Loading...' : getFilename(item.s3_key)}
                                </button>
                              </td>
                              <td>{formatPhoneNumber(item.phone_number)}</td>
                              <td>
                                <span className="message-type-badge">{item.channel || 'unknown'}</span>
                              </td>
                              <td>{item.content_type || 'N/A'}</td>
                              <td>{formatFileSize(item.file_size)}</td>
                              <td>{formatTimestamp(item.created_at)}</td>
                            </tr>
                            {isPreviewing && (
                              <tr>
                                <td colSpan="6" style={{ padding: '12px', backgroundColor: '#f9f9f9', textAlign: 'center' }}>
                                  {previewItem.contentType.startsWith('image/') ? (
                                    <img
                                      src={previewItem.url}
                                      alt={getFilename(item.s3_key)}
                                      style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '6px' }}
                                    />
                                  ) : (
                                    <video
                                      src={previewItem.url}
                                      controls
                                      style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '6px' }}
                                    />
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MediaPage;
